import React, { useState, useEffect } from 'react';
import { apiClient } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { 
  Settings, Save, Award, Calendar, Clock, RotateCcw, AlertCircle, CheckCircle, FileText, Bell
} from 'lucide-react';
import { toast } from 'sonner';

const DEFAULT_SETTINGS = {
  certificacao_validade_anos: 2,
  certificacao_score_minimo: 60,
  certificacao_auto_emissao: true,
  avaliacao_dimensoes_obrigatorias: 6,
  notificacao_expiracao_dias: 90
};

const DEFAULT_ALERTAS = {
  threshold_dias: 7,
  threshold_portal_dias: 30
};

export default function DefinicoesPage() {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [alertasSettings, setAlertasSettings] = useState(DEFAULT_ALERTAS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingAlertas, setSavingAlertas] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [hasAlertasChanges, setHasAlertasChanges] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const [certRes, alertasRes] = await Promise.all([
        apiClient.get('/definicoes/certificacao').catch(() => null),
        apiClient.get('/configuracoes/alertas-documentos').catch(() => null)
      ]);
      if (certRes?.data) setSettings(prev => ({ ...prev, ...certRes.data }));
      if (alertasRes?.data) setAlertasSettings(prev => ({ ...prev, ...alertasRes.data }));
    } catch (error) {
      console.log('Using default settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleAlertasChange = (key, value) => {
    setAlertasSettings(prev => ({ ...prev, [key]: value }));
    setHasAlertasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiClient.put('/definicoes/certificacao', settings);
      toast.success('Definições guardadas com sucesso');
      setHasChanges(false);
    } catch (error) {
      toast.error('Erro ao guardar definições');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAlertas = async () => {
    setSavingAlertas(true);
    try {
      await apiClient.put('/configuracoes/alertas-documentos', alertasSettings);
      toast.success('Configuração de alertas guardada');
      setHasAlertasChanges(false);
    } catch (error) {
      toast.error('Erro ao guardar configuração de alertas');
    } finally {
      setSavingAlertas(false);
    }
  };

  const handleReset = () => {
    setSettings(DEFAULT_SETTINGS);
    setHasChanges(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="definicoes-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Definições</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Configurações do sistema de certificação e avaliação</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleReset} disabled={saving}>
            <RotateCcw className="w-4 h-4 mr-2" />
            Repor Padrão
          </Button>
          <Button onClick={handleSave} disabled={saving || !hasChanges} className="bg-[#0066CC]">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'A guardar...' : 'Guardar'}
          </Button>
        </div>
      </div>

      {hasChanges && (
        <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-amber-600" />
          <span className="text-sm text-amber-700 dark:text-amber-400">
            Existem alterações por guardar
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Certificação */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-amber-500" />
              Certificação EA
            </CardTitle>
            <CardDescription>
              Configurações para emissão e validade dos certificados
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="validade">Validade do Certificado (anos)</Label>
              <Input
                id="validade"
                type="number"
                min="1"
                max="10"
                value={settings.certificacao_validade_anos}
                onChange={e => handleChange('certificacao_validade_anos', parseInt(e.target.value) || 2)}
                className="max-w-[150px]"
              />
              <p className="text-xs text-slate-500">
                Período de validade após a emissão do certificado
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="score_minimo">Score Mínimo para Certificação</Label>
              <div className="flex items-center gap-3">
                <Input
                  id="score_minimo"
                  type="number"
                  min="0"
                  max="100"
                  value={settings.certificacao_score_minimo}
                  onChange={e => handleChange('certificacao_score_minimo', parseInt(e.target.value) || 60)}
                  className="max-w-[150px]"
                />
                <Badge variant="outline">
                  {settings.certificacao_score_minimo >= 80 ? 'Excelente' :
                   settings.certificacao_score_minimo >= 60 ? 'Bom' :
                   settings.certificacao_score_minimo >= 40 ? 'Satisfatório' : 'Insuficiente'}
                </Badge>
              </div>
              <p className="text-xs text-slate-500">
                Instituições com score igual ou superior serão certificadas automaticamente
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="auto_emissao">Emissão Automática</Label>
                <input
                  id="auto_emissao"
                  type="checkbox"
                  checked={settings.certificacao_auto_emissao}
                  onChange={e => handleChange('certificacao_auto_emissao', e.target.checked)}
                  className="w-5 h-5 rounded"
                />
              </div>
              <p className="text-xs text-slate-500">
                Emitir certificado automaticamente quando a avaliação for aprovada com score suficiente
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Notificações */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#0066CC]" />
              Notificações
            </CardTitle>
            <CardDescription>
              Configurações de alertas e notificações
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="notif_expiracao">Dias de Antecedência para Expiração</Label>
              <Input
                id="notif_expiracao"
                type="number"
                min="7"
                max="365"
                value={settings.notificacao_expiracao_dias}
                onChange={e => handleChange('notificacao_expiracao_dias', parseInt(e.target.value) || 90)}
                className="max-w-[150px]"
              />
              <p className="text-xs text-slate-500">
                Enviar notificação de expiração X dias antes da data de validade
              </p>
            </div>

            <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <h4 className="font-medium text-sm mb-3">Resumo das Configurações</h4>
              <ul className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Certificados válidos por {settings.certificacao_validade_anos} ano(s)
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Score mínimo de {settings.certificacao_score_minimo} pontos
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Notificação {settings.notificacao_expiracao_dias} dias antes da expiração
                </li>
                <li className="flex items-center gap-2">
                  {settings.certificacao_auto_emissao ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-amber-500" />
                  )}
                  Emissão automática {settings.certificacao_auto_emissao ? 'ativa' : 'desativada'}
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Avaliação */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-slate-500" />
              Avaliação
            </CardTitle>
            <CardDescription>
              Parâmetros do sistema de avaliação
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="dimensoes">Número de Dimensões Obrigatórias</Label>
              <Input
                id="dimensoes"
                type="number"
                min="1"
                max="10"
                value={settings.avaliacao_dimensoes_obrigatorias}
                onChange={e => handleChange('avaliacao_dimensoes_obrigatorias', parseInt(e.target.value) || 6)}
                className="max-w-[150px]"
              />
              <p className="text-xs text-slate-500">
                Número mínimo de dimensões que um formulário de avaliação deve ter
              </p>
            </div>

            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200">
              <h4 className="font-medium text-sm text-blue-800 dark:text-blue-300 mb-2">
                Classificações Automáticas
              </h4>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between">
                  <span>Excelente</span>
                  <span className="font-mono">≥ 80 pontos</span>
                </div>
                <div className="flex justify-between">
                  <span>Bom</span>
                  <span className="font-mono">≥ 60 pontos</span>
                </div>
                <div className="flex justify-between">
                  <span>Satisfatório</span>
                  <span className="font-mono">≥ 40 pontos</span>
                </div>
                <div className="flex justify-between">
                  <span>Insuficiente</span>
                  <span className="font-mono">&lt; 40 pontos</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        {/* Alertas de Documentos */}
        <Card data-testid="card-alertas-documentos">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-orange-500" />
              Alertas de Documentos
            </CardTitle>
            <CardDescription>
              Limiar de dias para alertas visuais e do cron de documentos expirados
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="threshold_dias">Limiar Backoffice (dias)</Label>
              <div className="flex items-center gap-3">
                <Input
                  id="threshold_dias"
                  type="number"
                  min="1"
                  max="365"
                  value={alertasSettings.threshold_dias}
                  onChange={e => handleAlertasChange('threshold_dias', parseInt(e.target.value) || 7)}
                  className="max-w-[150px]"
                  data-testid="input-threshold-dias"
                />
                <Badge variant="outline" className={
                  alertasSettings.threshold_dias <= 7 ? 'border-orange-400 text-orange-600' :
                  alertasSettings.threshold_dias <= 14 ? 'border-amber-400 text-amber-600' : 'border-blue-400 text-blue-600'
                }>
                  {alertasSettings.threshold_dias <= 7 ? 'Alerta Rápido' :
                   alertasSettings.threshold_dias <= 14 ? 'Alerta Moderado' : 'Alerta Antecipado'}
                </Badge>
              </div>
              <p className="text-xs text-slate-500">
                Documentos com prazo inferior a X dias aparecerão no dashboard do backoffice e na Tab Processo das candidaturas
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="threshold_portal_dias">Limiar Portal das Instituições (dias)</Label>
              <div className="flex items-center gap-3">
                <Input
                  id="threshold_portal_dias"
                  type="number"
                  min="1"
                  max="365"
                  value={alertasSettings.threshold_portal_dias}
                  onChange={e => handleAlertasChange('threshold_portal_dias', parseInt(e.target.value) || 30)}
                  className="max-w-[150px]"
                  data-testid="input-threshold-portal-dias"
                />
                <Badge variant="outline" className="border-blue-400 text-blue-600">
                  {alertasSettings.threshold_portal_dias} dias
                </Badge>
              </div>
              <p className="text-xs text-slate-500">
                As instituições receberão alertas no seu portal com X dias de antecedência
              </p>
            </div>

            <div className="p-4 bg-orange-50 dark:bg-orange-900/10 rounded-lg border border-orange-200 dark:border-orange-800">
              <h4 className="font-medium text-sm text-orange-800 dark:text-orange-300 mb-2">Legenda de Urgência</h4>
              <div className="space-y-1.5 text-xs">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-red-500 shrink-0" />
                  <span className="text-red-700 dark:text-red-400">Expirado — prazo já ultrapassado (vermelho crítico)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-orange-500 shrink-0" />
                  <span className="text-orange-700 dark:text-orange-400">Crítico — prazo &lt; 7 dias (laranja)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-amber-400 shrink-0" />
                  <span className="text-amber-700 dark:text-amber-400">Aviso — prazo dentro do limiar configurado (amarelo)</span>
                </div>
              </div>
            </div>

            <Button onClick={handleSaveAlertas} disabled={savingAlertas || !hasAlertasChanges} className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-50" data-testid="btn-guardar-alertas">
              <Save className="w-4 h-4 mr-2" />
              {savingAlertas ? 'A guardar...' : 'Guardar Configuração de Alertas'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
